import {
  normalizeCoreGroupTitleEntries,
  normalizeNextCoreGroupNumber
} from '../objects/core_group_title_model.mjs?v=20260904c';
import {
  normalizeCoreGroupWorkflowStatus
} from '../objects/core_group_workflow_model.mjs?v=20260903a';

const DRAWING_SHEETS_SCHEMA_VERSION = 1;
const DRAWING_SHEETS_MAX_COUNT = 100;
const DEFAULT_DRAWING_SHEET_TITLE = 'Zeichnung Nr. 1';
const DEFAULT_DRAWING_SHEET_TITLE_PREFIX = 'Zeichnung Nr. ';
const DRAWING_SHEET_TITLE_MAX_LENGTH = 160;

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function cloneDrawingSheetValue(value) {
  if (value === undefined) return undefined;
  if (typeof structuredClone === 'function') return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
}

function normalizedText(value) {
  return String(value ?? '').trim().replace(/\s+/g, ' ');
}

function validateDrawingSheetTitle(value, options = {}) {
  const maxLength = Number.isSafeInteger(Number(options.maxLength)) && Number(options.maxLength) > 0
    ? Number(options.maxLength)
    : DRAWING_SHEET_TITLE_MAX_LENGTH;
  const title = normalizedText(value);
  if (!title) {
    return {
      valid: false,
      title: '',
      error: 'Der Titel der Zeichnungsebene darf nicht leer sein.'
    };
  }
  if (title.length > maxLength) {
    return {
      valid: false,
      title,
      error: `Der Titel der Zeichnungsebene darf höchstens ${maxLength} Zeichen enthalten.`
    };
  }
  if ([...title].some(character => {
    const codePoint = character.codePointAt(0);
    return codePoint <= 31 || codePoint === 127;
  })) {
    return {
      valid: false,
      title,
      error: 'Der Titel der Zeichnungsebene enthält ungültige Steuerzeichen.'
    };
  }
  return { valid: true, title, error: '' };
}

function normalizeDrawingSheetTitle(value, fallback = DEFAULT_DRAWING_SHEET_TITLE, options = {}) {
  const validation = validateDrawingSheetTitle(value, options);
  if (validation.valid) return validation.title;
  const fallbackValidation = validateDrawingSheetTitle(fallback, options);
  return fallbackValidation.valid ? fallbackValidation.title : DEFAULT_DRAWING_SHEET_TITLE;
}

function drawingSheetList(value) {
  if (Array.isArray(value)) return value;
  return Array.isArray(value?.sheets) ? value.sheets : [];
}

function suggestedDrawingSheetTitle(value, options = {}) {
  const sheets = drawingSheetList(value);
  const prefix = normalizeDrawingSheetTitle(
    options.prefix,
    DEFAULT_DRAWING_SHEET_TITLE_PREFIX
  ).replace(/\s*$/, ' ');
  const occupiedTitles = new Set(
    sheets
      .map(sheet => normalizedText(sheet?.title).toLocaleLowerCase())
      .filter(Boolean)
  );
  const numberedTitlePattern = /^zeichnung\s+nr\.?\s*(\d+)$/i;
  let highestNumber = 0;
  for (const sheet of sheets) {
    const match = normalizedText(sheet?.title).match(numberedTitlePattern);
    if (match) highestNumber = Math.max(highestNumber, Number(match[1]) || 0);
  }
  const nextDrawingSheetNumber = Number(value?.nextDrawingSheetNumber);
  const nextPersistentNumber = Number.isSafeInteger(nextDrawingSheetNumber)
    && nextDrawingSheetNumber > 0
    ? nextDrawingSheetNumber
    : 1;
  let number = Math.max(sheets.length + 1, highestNumber + 1, nextPersistentNumber);
  let candidate = `${prefix}${number}`;
  while (occupiedTitles.has(candidate.toLocaleLowerCase())) {
    number += 1;
    candidate = `${prefix}${number}`;
  }
  return candidate;
}

function normalizeDrawingSheetSidebarGroup(value) {
  if (!isPlainObject(value)) return null;
  const systemId = normalizedText(value.systemId ?? value.systemKey);
  const groupId = normalizedText(value.groupId ?? value.id);
  if (!systemId || !groupId) return null;
  const key = `${systemId}:${groupId}`;
  const displayName = normalizedText(
    value.displayName ?? value.label ?? value.name ?? groupId
  ) || groupId;
  const sidebarMeta = normalizedText(
    value.sidebarMeta
      ?? value.metaLabel
      ?? value.detailLabel
      ?? value.systemLabel
  );
  const groupTitleBase = normalizedText(value.groupTitleBase ?? value.baseDisplayName);
  const groupTitleSuffix = normalizedText(value.groupTitleSuffix ?? value.titleSuffix);
  const groupTitleEntryId = normalizedText(value.groupTitleEntryId);
  const groupNumber = Number(value.groupNumber);
  const workflowStatus = normalizeCoreGroupWorkflowStatus(value.workflowStatus);
  const memberIds = [...new Set([
    ...(Array.isArray(value.memberIds) ? value.memberIds : []),
    ...(Array.isArray(value.objectIds) ? value.objectIds : []),
    ...(Array.isArray(value.bomIds) ? value.bomIds : []),
    ...(Array.isArray(value.renderIds) ? value.renderIds : []),
    value.primaryObjectId
  ].map(normalizedText).filter(Boolean))];
  return {
    key,
    groupId,
    systemId,
    displayName,
    sidebarMeta,
    workflowStatus,
    ...(groupTitleEntryId ? { groupTitleEntryId } : {}),
    ...(groupTitleBase ? { groupTitleBase } : {}),
    ...(groupTitleSuffix ? { groupTitleSuffix } : {}),
    ...(Number.isSafeInteger(groupNumber) && groupNumber > 0 ? { groupNumber } : {}),
    ...(memberIds.length ? { memberIds } : {})
  };
}

function normalizeDrawingSheetSidebarGroups(value) {
  if (!Array.isArray(value)) return [];
  const groups = [];
  const seenKeys = new Set();
  for (const rawGroup of value) {
    const group = normalizeDrawingSheetSidebarGroup(rawGroup);
    if (!group || seenKeys.has(group.key)) continue;
    seenKeys.add(group.key);
    groups.push(group);
  }
  return groups;
}

function scrubDrawingSheetDocumentEnvelope(document) {
  if (!isPlainObject(document)) return null;
  // Der JSON-Fallback muss benutzerdefinierte Konvertierungen weiterhin vor
  // dem Bereinigen auf dem ursprünglichen Dokument ausführen.
  if (typeof structuredClone !== 'function') {
    const clone = cloneDrawingSheetValue(document);
    delete clone.drawingSheets;
    if (isPlainObject(clone.project)) delete clone.project.inputDrafts;
    return clone;
  }
  const envelope = { ...document };
  delete envelope.drawingSheets;
  if (isPlainObject(document.project)) {
    envelope.project = { ...document.project };
    delete envelope.project.inputDrafts;
  }
  return cloneDrawingSheetValue(envelope);
}

function normalizeDrawingSheetState(value) {
  if (!isPlainObject(value) || !isPlainObject(value.project)) return null;
  return scrubDrawingSheetDocumentEnvelope(value);
}

function defaultDrawingSheetIdFactory(prefix = 'drawing_sheet') {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return `${prefix}_${globalThis.crypto.randomUUID()}`;
  }
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}_${Date.now().toString(36)}`;
}

function uniqueDrawingSheetId(rawId, usedIds, idFactory, index) {
  const candidate = normalizedText(rawId);
  if (candidate && !usedIds.has(candidate)) return candidate;
  for (let attempt = 0; attempt < 20; attempt += 1) {
    const generated = normalizedText(idFactory('drawing_sheet', { index, attempt }));
    if (generated && !usedIds.has(generated)) return generated;
  }
  let fallback = `drawing_sheet_${index + 1}`;
  let suffix = 1;
  while (usedIds.has(fallback)) {
    suffix += 1;
    fallback = `drawing_sheet_${index + 1}_${suffix}`;
  }
  return fallback;
}

function uniqueDrawingSheetNumber(rawNumber, usedNumbers, index) {
  const numericNumber = Number(rawNumber);
  if (Number.isSafeInteger(numericNumber)
    && numericNumber > 0
    && !usedNumbers.has(numericNumber)) {
    usedNumbers.add(numericNumber);
    return numericNumber;
  }
  let fallback = Math.max(1, index + 1);
  while (usedNumbers.has(fallback)) fallback += 1;
  usedNumbers.add(fallback);
  return fallback;
}

function normalizeNextDrawingSheetNumber(value, sheets = []) {
  const highestAssignedNumber = (Array.isArray(sheets) ? sheets : []).reduce((highest, sheet) => {
    const number = Number(sheet?.number);
    return Number.isSafeInteger(number) && number > 0
      ? Math.max(highest, number)
      : highest;
  }, 0);
  const candidate = Number(value);
  return Number.isSafeInteger(candidate) && candidate > highestAssignedNumber
    ? candidate
    : highestAssignedNumber + 1;
}

function normalizeDrawingSheetMetadata(value, options = {}) {
  const source = isPlainObject(value) ? value : {};
  const index = Number.isSafeInteger(Number(options.index)) && Number(options.index) >= 0
    ? Number(options.index)
    : 0;
  const usedIds = options.usedIds instanceof Set ? options.usedIds : new Set();
  const idFactory = typeof options.idFactory === 'function'
    ? options.idFactory
    : defaultDrawingSheetIdFactory;
  const id = uniqueDrawingSheetId(source.id, usedIds, idFactory, index);
  usedIds.add(id);
  const usedNumbers = options.usedNumbers instanceof Set ? options.usedNumbers : new Set();
  const numberIndex = Number.isSafeInteger(Number(options.numberIndex))
    && Number(options.numberIndex) >= 0
    ? Number(options.numberIndex)
    : index;
  const number = uniqueDrawingSheetNumber(source.number, usedNumbers, numberIndex);
  const fallbackTitle = options.fallbackTitle
    || `${DEFAULT_DRAWING_SHEET_TITLE_PREFIX}${index + 1}`;
  const numericSortOrder = Number(source.sortOrder);
  const sortOrder = Number.isSafeInteger(numericSortOrder) && numericSortOrder >= 0
    ? numericSortOrder
    : index;
  return {
    id,
    number,
    title: normalizeDrawingSheetTitle(source.title, fallbackTitle),
    sortOrder,
    state: options.active === true ? null : normalizeDrawingSheetState(source.state),
    sidebarGroups: normalizeDrawingSheetSidebarGroups(source.sidebarGroups),
    coreGroupTitles: normalizeCoreGroupTitleEntries(source.coreGroupTitles)
  };
}

function selectActiveRawSheet(rawSheets, requestedActiveSheetId) {
  const requestedId = normalizedText(requestedActiveSheetId);
  const requested = rawSheets.find(sheet => normalizedText(sheet?.id) === requestedId);
  if (requested && sheetHasNullState(requested)) return requested;
  const explicitActive = rawSheets.find(sheet => sheetHasNullState(sheet));
  return explicitActive || requested || rawSheets[0] || null;
}

function sheetHasNullState(sheet) {
  return isPlainObject(sheet) && (sheet.state === null || sheet.state === undefined);
}

function normalizeDrawingSheets(value, options = {}) {
  const source = isPlainObject(value) ? value : {};
  const maxSheets = Number.isSafeInteger(Number(options.maxSheets)) && Number(options.maxSheets) > 0
    ? Math.min(Number(options.maxSheets), DRAWING_SHEETS_MAX_COUNT)
    : DRAWING_SHEETS_MAX_COUNT;
  const rawSheets = Array.isArray(source.sheets)
    ? source.sheets.filter(isPlainObject).slice(0, maxSheets)
    : [];
  const idFactory = typeof options.idFactory === 'function'
    ? options.idFactory
    : defaultDrawingSheetIdFactory;
  const usedIds = new Set();
  const usedNumbers = new Set();

  if (!rawSheets.length) {
    const sheet = normalizeDrawingSheetMetadata({}, {
      active: true,
      idFactory,
      index: 0,
      numberIndex: 0,
      usedIds,
      usedNumbers,
      fallbackTitle: options.defaultTitle || DEFAULT_DRAWING_SHEET_TITLE
    });
    sheet.sidebarGroups = normalizeDrawingSheetSidebarGroups(options.sidebarGroups);
    return {
      schemaVersion: DRAWING_SHEETS_SCHEMA_VERSION,
      nextDrawingSheetNumber: 2,
      nextCoreGroupNumber: 1,
      activeSheetId: sheet.id,
      sheets: [sheet]
    };
  }

  const activeRawSheet = selectActiveRawSheet(rawSheets, source.activeSheetId);
  const normalizedSheets = [];
  for (const [index, rawSheet] of rawSheets.entries()) {
    const active = rawSheet === activeRawSheet;
    const sheet = normalizeDrawingSheetMetadata(rawSheet, {
      active,
      idFactory,
      index,
      numberIndex: normalizedSheets.length,
      usedIds,
      usedNumbers
    });
    // Ein inaktiver Eintrag ohne ProjectDocument kann nicht wiederhergestellt
    // werden. Solche beschädigten Platzhalter werden bewusst nicht übernommen.
    if (!active && !sheet.state) {
      usedNumbers.delete(sheet.number);
      continue;
    }
    if (active && Array.isArray(options.sidebarGroups)) {
      sheet.sidebarGroups = normalizeDrawingSheetSidebarGroups(options.sidebarGroups);
    }
    normalizedSheets.push(sheet);
  }

  if (!normalizedSheets.length) {
    return normalizeDrawingSheets(null, options);
  }
  let activeSheet = normalizedSheets.find(sheet => sheet.state === null);
  if (!activeSheet) {
    activeSheet = normalizedSheets[0];
    activeSheet.state = null;
  }
  return {
    schemaVersion: DRAWING_SHEETS_SCHEMA_VERSION,
    nextDrawingSheetNumber: normalizeNextDrawingSheetNumber(
      source.nextDrawingSheetNumber,
      normalizedSheets
    ),
    nextCoreGroupNumber: normalizeNextCoreGroupNumber(
      source.nextCoreGroupNumber,
      normalizedSheets.flatMap(sheet => sheet.coreGroupTitles || [])
    ),
    activeSheetId: activeSheet.id,
    sheets: normalizedSheets
  };
}

function normalizeDrawingSheetDocument(document, options = {}) {
  if (!isPlainObject(document) || !isPlainObject(document.project)) {
    throw new TypeError('Zeichnungsdokument fehlt oder ist ungültig.');
  }
  if (typeof structuredClone !== 'function') {
    const normalized = cloneDrawingSheetValue(document);
    normalized.drawingSheets = normalizeDrawingSheets(normalized.drawingSheets, options);
    return normalized;
  }
  const envelope = { ...document };
  delete envelope.drawingSheets;
  const normalized = cloneDrawingSheetValue(envelope);
  // Inaktive Zustände werden bereits beim Normalisieren unabhängig geklont.
  normalized.drawingSheets = normalizeDrawingSheets(document.drawingSheets, options);
  return normalized;
}

export {
  DEFAULT_DRAWING_SHEET_TITLE,
  DEFAULT_DRAWING_SHEET_TITLE_PREFIX,
  DRAWING_SHEETS_MAX_COUNT,
  DRAWING_SHEETS_SCHEMA_VERSION,
  DRAWING_SHEET_TITLE_MAX_LENGTH,
  cloneDrawingSheetValue,
  defaultDrawingSheetIdFactory,
  normalizeDrawingSheetDocument,
  normalizeDrawingSheetMetadata,
  normalizeDrawingSheetSidebarGroup,
  normalizeDrawingSheetSidebarGroups,
  normalizeDrawingSheetState,
  normalizeDrawingSheetTitle,
  normalizeDrawingSheets,
  normalizeNextDrawingSheetNumber,
  scrubDrawingSheetDocumentEnvelope,
  suggestedDrawingSheetTitle,
  validateDrawingSheetTitle
};
